import { useState } from 'react'
import { cn } from '@/lib/utils'
import { ADULT_QUADRANTS, CHILD_QUADRANTS } from '@/lib/constants'

/**
 * Renders a clickable dental chart using FDI two-digit tooth notation.
 *
 * Props:
 *  - value: { adult_teeth: number[], child_teeth: number[] }
 *  - onChange: (next) => void
 *  - mode: 'adult' | 'child' (which dentition the chart starts with)
 *  - showToggle: boolean — show the Adult / Child toggle (default true)
 *  - getToothColor: (toothNumber, dentition) => className | null
 *      Optional. Lets graded charts paint teeth differently per grade.
 *  - readOnly: boolean
 */
export function DentalChart({
  value = { adult_teeth: [], child_teeth: [] },
  onChange,
  mode: modeProp,
  showToggle = true,
  getToothColor,
  readOnly = false,
  className,
}) {
  const [internalMode, setInternalMode] = useState(modeProp || 'adult')
  const mode = modeProp || internalMode
  const setMode = (m) => {
    if (!modeProp) setInternalMode(m)
  }

  const safe = {
    adult_teeth: Array.isArray(value?.adult_teeth) ? value.adult_teeth : [],
    child_teeth: Array.isArray(value?.child_teeth) ? value.child_teeth : [],
  }

  const isSelected = (n, dentition) =>
    dentition === 'adult'
      ? safe.adult_teeth.includes(n)
      : safe.child_teeth.includes(n)

  const toggle = (n) => {
    if (readOnly) return
    if (mode === 'adult') {
      const next = safe.adult_teeth.includes(n)
        ? safe.adult_teeth.filter((x) => x !== n)
        : [...safe.adult_teeth, n]
      onChange?.({ ...safe, adult_teeth: next })
    } else {
      const next = safe.child_teeth.includes(n)
        ? safe.child_teeth.filter((x) => x !== n)
        : [...safe.child_teeth, n]
      onChange?.({ ...safe, child_teeth: next })
    }
  }

  const Q = mode === 'adult' ? ADULT_QUADRANTS : CHILD_QUADRANTS

  const renderTooth = (n) => {
    const selected = isSelected(n, mode)
    const color = getToothColor?.(n, mode)
    return (
      <button
        key={`${mode}-${n}`}
        type="button"
        onClick={() => toggle(n)}
        disabled={readOnly}
        className={cn(
          'group relative flex h-10 w-10 sm:h-11 sm:w-11 items-center justify-center rounded-lg text-[11px] sm:text-xs font-bold transition-all',
          'ring-1 ring-inset',
          color
            ? color
            : selected
              ? 'bg-primary-700 text-cream ring-primary-800 shadow-soft'
              : 'bg-white text-ink-700 ring-ink-200 hover:bg-primary-50 hover:ring-primary-300',
          readOnly && 'cursor-default opacity-90',
        )}
      >
        {n}
      </button>
    )
  }

  return (
    <div className={cn('w-full', className)}>
      {/* Toggle */}
      {showToggle && !modeProp && (
        <div className="mb-3 inline-flex rounded-xl bg-ink-100/70 p-1 ring-1 ring-ink-200">
          {[
            { v: 'adult', label: 'Adult (32)' },
            { v: 'child', label: 'Child (20)' },
          ].map((opt) => (
            <button
              key={opt.v}
              type="button"
              onClick={() => setMode(opt.v)}
              className={cn(
                'rounded-lg px-3 py-1.5 text-xs font-semibold transition-all',
                mode === opt.v
                  ? 'bg-white text-primary-800 shadow-soft ring-1 ring-ink-200'
                  : 'text-ink-600 hover:text-ink-900',
              )}
            >
              {opt.label}
            </button>
          ))}
        </div>
      )}

      {/* Selection count chips */}
      <div className="mb-3 flex flex-wrap gap-2 text-[10px] font-bold uppercase tracking-wider text-ink-500">
        <span className="rounded-full bg-white px-2 py-1 ring-1 ring-ink-200">
          Adult selected: <span className="text-primary-700">{safe.adult_teeth.length}</span>
        </span>
        <span className="rounded-full bg-white px-2 py-1 ring-1 ring-ink-200">
          Child selected: <span className="text-primary-700">{safe.child_teeth.length}</span>
        </span>
      </div>

      <div className="overflow-x-auto rounded-2xl bg-gradient-to-b from-cream to-white p-4 ring-1 ring-ink-100">
        <div className="mx-auto w-fit">
          <p className="mb-2 text-center text-[10px] font-bold uppercase tracking-[0.18em] text-ink-400">
            Upper jaw
          </p>
          <div className="flex items-center justify-center gap-1">
            <div className="flex gap-1">{Q.upperRight.map(renderTooth)}</div>
            <div className="mx-2 h-10 w-px bg-ink-200" />
            <div className="flex gap-1">{Q.upperLeft.map(renderTooth)}</div>
          </div>

          <div className="my-3 flex items-center gap-2">
            <span className="h-px flex-1 bg-ink-200" />
            <span className="text-[9px] font-bold uppercase tracking-[0.2em] text-ink-400">
              Midline
            </span>
            <span className="h-px flex-1 bg-ink-200" />
          </div>

          <div className="flex items-center justify-center gap-1">
            <div className="flex gap-1">{Q.lowerRight.map(renderTooth)}</div>
            <div className="mx-2 h-10 w-px bg-ink-200" />
            <div className="flex gap-1">{Q.lowerLeft.map(renderTooth)}</div>
          </div>
          <p className="mt-2 text-center text-[10px] font-bold uppercase tracking-[0.18em] text-ink-400">
            Lower jaw
          </p>
        </div>
      </div>

      {/* Reference axes */}
      <div className="mt-2 flex justify-between text-[10px] font-semibold text-ink-400">
        <span>Right</span>
        <span>Left</span>
      </div>
    </div>
  )
}
